import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import axiosInstance from "../../axiosConfig"; // Adjust the path as needed
import './ClubDashboard.css';

function TabNavigation({ activeTab, setActiveTab }) {
  const tabs = [
    { id: "nearby", label: "Nearby Users" },
    { id: "pending", label: "Pending Requests" },
    { id: "approved", label: "Approved Requests" },
  ];

  return (
    <div className="tab-navigation">
      {tabs.map((tab) => (
        <span
          key={tab.id}
          className={`tab ${activeTab === tab.id ? "active" : ""}`}
          onClick={() => setActiveTab(tab.id)}
        >
          {tab.label}
        </span>
      ))}
    </div>
  );
}

const formatIncomeRange = (range) => {
  if (range === "1000000+") {
    return "$1M+";
  }
  const [low, high] = range.split("-");
  const formattedHigh = high === "1000000" ? "$1M" : `$${(high / 1000).toFixed()}K`;
  return `$${(low / 1000).toFixed()}K - ${formattedHigh}`;
};

const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
};

function ClubDashboard() {
  const { clubId } = useParams();
  const [activeTab, setActiveTab] = useState("nearby");
  const [clubData, setClubData] = useState(null);
  const [nearbyUsers, setNearbyUsers] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [approvedRequests, setApprovedRequests] = useState([]);
  const [error, setError] = useState("");
  const [radius, setRadius] = useState(20); // Default radius in miles

  useEffect(() => {
    const fetchClubData = async () => {
      try {
        const response = await axiosInstance.get(`myclub/dashboard/`, {
          params: { club_id: clubId },
        });
        setClubData(response.data);
      } catch (error) {
        setError("Failed to fetch club data. Please try again.");
      }
    };

    fetchClubData();
  }, [clubId]);

  useEffect(() => {
    if (activeTab === "nearby") {
      const fetchNearbyUsers = async () => {
        try {
          const response = await axiosInstance.get('myclub/nearby-users/', {
            params: { radius },
          });
          setNearbyUsers(response.data.nearby_users);
        } catch (error) {
          setError("Failed to fetch nearby users. Please try again.");
        }
      };

      fetchNearbyUsers();
    } else if (activeTab === "pending") {
      const fetchPendingRequests = async () => {
        try {
          const response = await axiosInstance.get('myclub/requests/pending/');
          setPendingRequests(response.data.requests);
        } catch (error) {
          setError("Failed to fetch pending requests. Please try again.");
        }
      };

      fetchPendingRequests();
    } else if (activeTab === "approved") {
      const fetchApprovedRequests = async () => {
        try {
          const response = await axiosInstance.get('myclub/requests/approved/');
          setApprovedRequests(response.data.requests);
        } catch (error) {
          setError("Failed to fetch approved requests. Please try again.");
        }
      };

      fetchApprovedRequests();
    }
  }, [activeTab, radius]);

  const handleRequestInfo = async (userId) => {
    try {
      const response = await axiosInstance.post('myclub/interactions/create', {
        user_id: userId,
      });
      alert(response.data.message);
    } catch (error) {
      alert("Failed to send interaction request. Please try again.");
    }
  };

  if (error) return <p style={{ color: "red" }}>{error}</p>;

  if (!clubData) return <p>Loading...</p>;

  return (
      <div className="course-detail">
        <div className="course-info-container">
          <div className="course-header-container">
            <h1 className="course-name">{clubData.name}</h1>
          </div>
          <hr className="section-divider"/>
          <div className="course-info-2">
            <p><strong>Address:</strong>{clubData.address}</p>
            <p><strong>Phone: </strong>{clubData.phone_number}</p>
            <p><strong>Website: </strong><a href={clubData.website}>{clubData.website}</a></p>
          </div>
        </div>

        <TabNavigation activeTab={activeTab} setActiveTab={setActiveTab}/>

        <div className="tab-content">
          {activeTab === "nearby" && (
              <div className="nearby-users">
                <h2>Nearby Users</h2>
                <div className="slider-container">
                  <label>Search Radius: {radius} miles</label>
                  <Slider
                    min={1}
                    max={100}
                    value={radius}
                    onChange={setRadius}
                    step={1}
                    trackStyle={{ backgroundColor: "#346566", height: 6 }}
                    handleStyle={{
                      borderColor: "#346566",
                      height: 18,
                      width: 18,
                      backgroundColor: "#fff",
                    }}
                    railStyle={{ backgroundColor: "#ddd", height: 6 }}
                  />
                </div>
                {nearbyUsers.length === 0 ? (
                    <p>No users found nearby.</p>
                ) : (
                    <table>
                      <thead>
                      <tr>
                        <th>Age</th>
                        <th>Household Income</th>
                        <th>Marital Status</th>
                        <th>Number of Children</th>
                        <th>Current Club Member</th>
                        <th>Distance (miles)</th>
                        <th>Request Info</th>
                      </tr>
                      </thead>
                      <tbody>
                      {nearbyUsers.map((user, index) => (
                          <tr key={index}>
                            <td>{user.age || "N/A"}</td>
                            <td>{formatIncomeRange(user.household_income) || "N/A"}</td>
                            <td>{user.marital_status || "N/A"}</td>
                            <td>{user.number_of_children || "0"}</td>
                            <td>{user.current_club_member || "N/A"}</td>
                            <td>{user.distance || "N/A"}</td>
                            <td>
                              <button
                                  onClick={() => handleRequestInfo(user.id)}
                                  className="request-info-button"
                              >
                                Request Info
                              </button>
                            </td>
                          </tr>
                      ))}
                      </tbody>
                    </table>
                )}
              </div>
          )}

          {activeTab === "pending" && (
              <div className="pending-requests">
                <h2>Pending Requests</h2>
                {pendingRequests.length === 0 ? (
                    <p>No pending requests.</p>
                ) : (
                    <table>
                      <thead>
                      <tr>
                        <th>Age</th>
                        <th>Household Income</th>
                        <th>Marital Status</th>
                        <th>Number of Children</th>
                        <th>Current Club Member</th>
                        <th>Status</th>
                        <th>Created</th>
                      </tr>
                      </thead>
                      <tbody>
                      {pendingRequests.map((request, index) => (
                          <tr key={index}>
                            <td>{request.age || "N/A"}</td>
                            <td>{formatIncomeRange(request.household_income) || "N/A"}</td>
                            <td>{request.marital_status || "N/A"}</td>
                            <td>{request.number_of_children || "0"}</td>
                            <td>{request.current_club_member || "N/A"}</td>
                            <td>{request.status}</td>
                            <td>{formatDate(request.created_at)}</td>
                          </tr>
                      ))}
                      </tbody>
                    </table>
                )}
              </div>
          )}

          {activeTab === "approved" && (
              <div className="approved-requests">
                <h2>Approved Requests</h2>
                {approvedRequests.length === 0 ? (
                    <p>No approved requests.</p>
                ) : (
                    <table>
                      <thead>
                      <tr>
                        <th>Full Name</th>
                        <th>Email</th>
                        <th>Phone Number</th>
                        <th>Age</th>
                        <th>Household Income</th>
                        <th>Marital Status</th>
                        <th>Number of Children</th>
                        <th>Current Club Member</th>
                        <th>Status</th>
                      </tr>
                      </thead>
                      <tbody>
                      {approvedRequests.map((request, index) => (
                          <tr key={index}>
                            <td>{request.full_name}</td>
                            <td>{request.email}</td>
                            <td>{request.phone_number || "N/A"}</td>
                            <td>{request.age || "N/A"}</td>
                            <td>{formatIncomeRange(request.household_income) || "N/A"}</td>
                            <td>{request.marital_status || "N/A"}</td>
                            <td>{request.number_of_children || "0"}</td>
                            <td>{request.current_club_member || "N/A"}</td>
                            <td>{request.status}</td>
                          </tr>
                      ))}
                      </tbody>
                    </table>
                )}
              </div>
          )}
        </div>
      </div>
  );
}

export default ClubDashboard;
